import Blank from '@doltech/ui/lib/figma/BlankEditor/Result/Blank';
import { BlankItemResult } from '@doltech/ui/lib/figma/BlankEditor/type';
import { withStoreRequiredForEditor } from '@doltech/ui/lib/figma/BlogEditor/core/withStoreRequiredForEditor';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box } from '@doltech/ui/lib/figma/Common/Box';
import { DolEditor } from '@doltech/ui/lib/figma/DolEditor/DolEditor';
import { EDITABLE_BLANK_VOID } from '@doltech/ui/lib/figma/DolEditor/elements/editable-blank-voids/types';
import { tableDatav1ToV2 } from '@doltech/ui/lib/figma/DolEditor/elements/table-blog/transforms/tableV1ToV2';
import { getClosestTable } from '@doltech/ui/lib/figma/DolEditor/elements/table/queries/getClosestTable';
import { isRowHeading } from '@doltech/ui/lib/figma/DolEditor/elements/table/queries/isRowHeading';
import { TableElement } from '@doltech/ui/lib/figma/DolEditor/elements/table/types';
import { compose } from '@doltech/utils/lib/compose';
import { TestModeEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { createEditor, Editor, Node } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact } from 'slate-react';
import styled from 'styled-components';

import { BlankItemInstance } from '../../../question-data-model/Question/Blank/blank-item.store';
import { useTableCompletionPluginAndEditorChains } from '../../../student-do-test-questions/useTableCompletionPluginAndEditorChains';
import { TableCompletionQuestionInstance } from '../../model/Question/result-table-completion-question.store';
import { ExtraQuestionProps } from './BaseQuestion';

interface TableCompletionResultProps extends React.HTMLAttributes<HTMLDivElement> {
  data: TableCompletionQuestionInstance & ExtraQuestionProps;
  testMode: TestModeEnum;
}

export const TableCompletionQuestionResult = compose(
  withStoreRequiredForEditor({}),
  observer
)((props: TableCompletionResultProps) => {
  const { data, testMode, ...rest } = props;
  const { blanks, editorValue, no, question, index } = data;
  const [collapsedValue, setCollapsedValue] = React.useState<Node[] | null>(null);

  const editorBlanks = React.useMemo(() => {
    if (data) {
      return data.blanks.map((bl, idx) => ({ blankId: bl.id, blankIndex: idx }));
    }
    return [];
  }, [data]);

  const { plugins, editorChains } = useTableCompletionPluginAndEditorChains({ editorBlanks, no });

  const renderTable = React.useCallback(
    (value, showAllResult = false) => {
      return (
        <>
          <Title>{question}</Title>
          <DolEditor
            initialValue={tableDatav1ToV2(value)}
            editorChains={editorChains}
            plugins={plugins}
            renderElementDeps={[blanks]}
            renderElement={[
              (renderProps) => {
                if (renderProps.element.type === EDITABLE_BLANK_VOID) {
                  const blankId = renderProps.element.children[0].blankId as string;
                  const blank = blanks.find((bl) => bl.id === blankId);
                  return (
                    <CustomStyle>
                      <Blank
                        blank={blank as BlankItemResult}
                        className="tbl-blank-result"
                        isViewResult={showAllResult || blank.key === index}
                        testMode={testMode}
                      />
                    </CustomStyle>
                  );
                }

                return undefined;
              },
            ]}
          />
        </>
      );
    },
    [question, editorChains, plugins, blanks, index, testMode]
  );

  return (
    <Container className="dol-question table-completion-answer" {...rest}>
      {collapsedValue === null ? (
        <HiddenEditor
          value={JSON.parse(editorValue)}
          blank={blanks.find((bl) => bl.no === no)}
          onFilter={setCollapsedValue}
        />
      ) : (
        renderTable(JSON.parse(editorValue), true)
      )}
    </Container>
  );
});

const Container = styled(Box)``;

const Title = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: ${colorsV2.black100};
  margin-bottom: 24px;
`;

const CustomStyle = styled.div`
  display: inline-block;

  .tbl-blank-result {
    display: flex;
    flex-flow: row wrap;
    margin: 0 4px 0 8px;
    > div {
      margin-right: 4px;
    }
  }
`;

interface HiddenEditorProps {
  value: Node[];
  blank: BlankItemInstance;
  onFilter: (value: Node[]) => void;
}

/**
 * This component used to filter the row of current blank
 * 2 cases:
 * - first row is header: show header + filtered row
 * - first row is not header: show filtered row
 */
export const HiddenEditor = React.memo((props: HiddenEditorProps) => {
  const { value, blank, onFilter } = props;
  const editor = React.useMemo(() => withHistory(withReact<any>(createEditor())), []);

  React.useEffect(() => {
    const entries = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (node: any) => {
          return node?.blankId === blank.id;
        },
      })
    );

    if (entries.length > 0) {
      const [[, blankPath]] = entries;
      const [tr] = Editor.above(editor, {
        at: blankPath,
        match: (node: any) => node.type === TableElement.TR,
      });
      const tableEntry = getClosestTable(editor, tr);
      const isFirstRowHeading = isRowHeading(editor, tableEntry?.[0].id);
      const [table] = value;
      const newTable = { ...table };
      if (isFirstRowHeading) {
        newTable.children = [table.children[0], tr];
      } else {
        newTable.children = [tr];
      }

      onFilter([newTable]);
    }
  }, [blank, editor, value, onFilter]);

  return (
    <HiddenEditorContainer>
      <Slate editor={editor} initialValue={value} onChange={() => {}}>
        <Editable />
      </Slate>
    </HiddenEditorContainer>
  );
});

const HiddenEditorContainer = styled.div`
  position: fixed;
  top: -99999px;
  left: -99999px;
`;
